import { useState, useEffect } from "react";
import { UsersTable } from "./users-table/users-table";
import { Box, Button, Typography, Modal, useTheme } from "@mui/material";
import { CreateMessageForm } from "./create-message-form/create-message-form";
import { AdminCreds } from "../../types";
import { deleteUsers, getUsers } from "../../axios/admin";
import { FaPlus } from "react-icons/fa";
import { CreateUserForm } from "./create-user-form/create-user-form";
import { CustomTypography } from "../custom-typography/custom-typography";
import { UploadAssessmentModal } from "./upload-assessment-modal/upload-assessment-modal";
import { EditContestModal } from "./edit-contest-modal/edit-contest-modal";
import { DeleteContestModal } from "./delete-contest-modal/delete-contest-modal";

export const AdminPanelContainer = ({
  adminCreds,
}: {
  adminCreds: AdminCreds;
}) => {
  const [selectedLogins, setSelectedLogins] = useState<string[]>([]);
  const [isMessageFormOpen, setIsMessageFormOpen] = useState(false);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [isAssessmentUploadOpen, setIsAssessmentUploadOpen] = useState(false);
  const [isContestEditOpen, setIsContestEditOpen] = useState(false);
  const [isContestDeleteOpen, setIsContestDeleteOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      const usersData = await getUsers(adminCreds);
      setUsers(usersData);
      setLoading(false);
    };

    fetchUsers();
  }, []);

  const handleDeleteUsers = async () => {
    if (selectedLogins.length === 0) return;

    try {
      await deleteUsers(selectedLogins, adminCreds);
      setLoading(true);
      const usersData = await getUsers(adminCreds);
      setUsers(usersData);
      setLoading(false);
    } catch (error) {
      console.error("Error deleting users:", error);
    }
  };

  const handleMessageFormOpen = () => {
    setIsMessageFormOpen(true);
  };

  const handleMessageFormClose = () => {
    setIsMessageFormOpen(false);
  };

  const handleUserFormOpen = () => {
    setIsUserFormOpen(true);
  };

  const handleUserFormClose = () => {
    setIsUserFormOpen(false);
  };

  const actionButtonBaseSx = {
    height: { xs: "32px", sm: "40px" },
    borderRadius: { xs: "16px", sm: "20px" },
    fontSize: { xs: "0.6875rem", sm: "0.875rem" },
    px: { xs: "0.625rem", sm: "1.25rem" },
    minWidth: { xs: 0, sm: "auto" },
    flex: { xs: 1, sm: "none" },
    marginY: { xs: "0.5rem", sm: "1rem" },
    textTransform: "none",
    boxShadow: "none",
    whiteSpace: "nowrap",
    gap: { xs: "0.25rem", sm: "0.5rem" },
  } as const;

  return (
    <Box
      sx={{
        maxWidth: "90%",
        width: "72rem",
        flex: 1,
        minHeight: 0,
        height: "100%",
        paddingY: { xs: "1.5rem", sm: "3rem", md: "5rem" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: { xs: "nowrap", sm: "wrap" },
          gap: { xs: "0.375rem", sm: "1rem" },
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={handleUserFormOpen}
          sx={{
            ...actionButtonBaseSx,
            backgroundColor: "rgb(25, 118, 210)",
            display: "flex",
            flexDirection: "row",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.9)",
              boxShadow: "none",
            },
          }}
        >
          <FaPlus size={12} />
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Create new user
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            New user
          </Box>
        </Button>

        <Button
          variant="outlined"
          disabled={selectedLogins.length === 0}
          onClick={handleMessageFormOpen}
          sx={{
            ...actionButtonBaseSx,
            color: "rgb(25, 118, 210)",
            borderColor: "rgb(25, 118, 210)",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.1)",
              boxShadow: "none",
            },
            "&[disabled]": {
              color: "gray",
              borderColor: "gray",
            },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Send a message
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            Message
          </Box>
        </Button>

        <Button
          variant="outlined"
          onClick={() => setIsAssessmentUploadOpen(true)}
          sx={{
            ...actionButtonBaseSx,
            color: "rgb(25, 118, 210)",
            borderColor: "rgb(25, 118, 210)",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.1)",
              boxShadow: "none",
            },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Upload contest
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            Contest
          </Box>
        </Button>

        <Button
          variant="outlined"
          onClick={() => setIsContestEditOpen(true)}
          sx={{
            ...actionButtonBaseSx,
            color: "rgb(25, 118, 210)",
            borderColor: "rgb(25, 118, 210)",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.1)",
              boxShadow: "none",
            },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Edit contest
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            Edit
          </Box>
        </Button>

        <Button
          variant="outlined"
          onClick={() => setIsContestDeleteOpen(true)}
          sx={{
            ...actionButtonBaseSx,
            color: "rgb(210, 25, 25)",
            borderColor: "rgb(210, 25, 25)",
            "&:hover": {
              backgroundColor: "rgba(210, 25, 25, 0.1)",
              boxShadow: "none",
            },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Delete contest
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            Del contest
          </Box>
        </Button>

        <Button
          variant="contained"
          onClick={() => setIsDeleteModalOpen(true)}
          disabled={selectedLogins.length === 0}
          sx={{
            ...actionButtonBaseSx,
            backgroundColor: "rgb(210, 25, 25)",
            display: "flex",
            flexDirection: "row",
            "&:hover": {
              backgroundColor: "rgba(210, 25, 25, 0.8)",
              boxShadow: "none",
            },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Delete selected user(s)
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            Delete
          </Box>
        </Button>
      </Box>
      {users && users.length > 0 && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            mt: 1,
          }}
        >
          <UsersTable
            selectedLogins={selectedLogins}
            setSelectedLogins={setSelectedLogins}
            users={users}
          />
        </Box>
      )}
      {loading && (
        <Typography
          variant="h6"
          sx={{
            mt: "1.5rem",
            textAlign: "center",
            color: theme.palette.adminPanel.textColor,
          }}
        >
          Loading users...
        </Typography>
      )}
      <CreateMessageForm
        open={isMessageFormOpen}
        onClose={handleMessageFormClose}
        adminCreds={adminCreds}
        selectedLogins={selectedLogins}
      />
      <CreateUserForm
        open={isUserFormOpen}
        onClose={handleUserFormClose}
        adminCreds={adminCreds}
      />
      <UploadAssessmentModal
        open={isAssessmentUploadOpen}
        onClose={() => setIsAssessmentUploadOpen(false)}
        adminCreds={adminCreds}
      />
      <EditContestModal
        open={isContestEditOpen}
        onClose={() => setIsContestEditOpen(false)}
        adminCreds={adminCreds}
      />
      <DeleteContestModal
        open={isContestDeleteOpen}
        onClose={() => setIsContestDeleteOpen(false)}
        adminCreds={adminCreds}
      />

      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: "16px",
            padding: "2rem 5rem",
            boxShadow: 24,
            backgroundColor: theme.palette.modalColors.bgColor,
            maxHeight: { xs: "80dvh", sm: "80dvh", md: "80dvh" },
            overflowY: "auto",
          }}
        >
          <CustomTypography
            fontSize={20}
            fontWeight={600}
            sx={{
              color: theme.palette.adminPanel.textColor,
            }}
          >
            Are you sure?
          </CustomTypography>

          <CustomTypography
            fontSize={14}
            sx={{
              color: theme.palette.adminPanel.textColor,
            }}
          >
            <span key="gen-span">Certain users will be deleted:</span>
            <br key="line-break" />
            {selectedLogins.map((login, index) => (
              <>
                <span key={login}>
                  {index + 1}
                  {") "}
                  {login}
                </span>
                <br />
              </>
            ))}
          </CustomTypography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: "1rem",
            }}
          >
            <Button
              variant="outlined"
              sx={{
                height: "40px",
                borderRadius: "20px",
                textTransform: "none",
                color: "rgb(25, 118, 210)",
                borderColor: "rgb(25, 118, 210)",
                boxShadow: "none",
                marginY: "1rem",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.1)",
                  boxShadow: "none",
                },
                "&[disabled]": {
                  color: "gray",
                  borderColor: "gray",
                },
              }}
              onClick={() => setIsDeleteModalOpen(false)}
            >
              No, cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                height: "40px",
                borderRadius: "20px",
                textTransform: "none",
                backgroundColor: "rgb(210, 25, 25)",
                boxShadow: "none",
                marginY: "1rem",
                display: "flex",
                flexDirection: "row",
                gap: "0.5rem",
                "&:hover": {
                  backgroundColor: "rgba(210, 25, 25, 0.8)",
                  boxShadow: "none",
                },
              }}
              onClick={() => {
                handleDeleteUsers();
                setIsDeleteModalOpen(false);
              }}
            >
              {"Yes, delete selected user(s)"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
